import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Maximize2, Loader2, Clock, CheckCircle } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function IncidentsWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      const [incidentsData, statsData] = await Promise.all([
        apiRequest('/incidents?limit=5'),
        apiRequest('/incidents/stats')
      ]);
      setIncidents(incidentsData.incidents || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      default:
        return <Clock className="h-3 w-3 text-amber-500" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="incidents-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
          <h3 className="font-semibold text-slate-900">Incidents</h3>
          {stats?.open > 0 && (
            <Badge variant="destructive" className="text-xs">{stats.open} open</Badge>
          )}
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-4 text-center text-sm">
          <div className="p-2 bg-red-50 rounded">
            <div className="font-semibold text-red-700">{stats.open || 0}</div>
            <div className="text-xs text-red-600">Open</div>
          </div>
          <div className="p-2 bg-amber-50 rounded">
            <div className="font-semibold text-amber-700">{stats.investigating || 0}</div>
            <div className="text-xs text-amber-600">Investigating</div>
          </div>
          <div className="p-2 bg-green-50 rounded">
            <div className="font-semibold text-green-700">{stats.resolved || 0}</div>
            <div className="text-xs text-green-600">Resolved</div>
          </div>
        </div>
      )}

      {/* Recent Incidents */}
      {incidents.length > 0 ? (
        <div className="space-y-2">
          {incidents.slice(0, 3).map(incident => (
            <div 
              key={incident.id} 
              className="p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
              onClick={() => navigate(`/admin/incidents`)}
            >
              <div className="flex items-center gap-2">
                {getStatusIcon(incident.status)}
                <span className="text-sm font-medium text-slate-900 truncate flex-1">
                  {incident.title}
                </span>
                <Badge className={`text-xs ${getSeverityColor(incident.severity)}`}>
                  {incident.severity}
                </Badge>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {incident.store} • {new Date(incident.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-slate-500 text-sm">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
          <p>No recent incidents</p>
        </div>
      )}
    </div>
  );
}
